// Front-end error tracking (Sentry) — INERT unless REACT_APP_SENTRY_DSN is
// set at build time. Without it, @sentry/react is never downloaded (it is
// a separate lazy chunk) and every export here is a no-op.
//
// With it: unhandled errors and promise rejections are captured (Sentry's
// global handlers). Events are scrubbed before sending — no request
// bodies, cookies, headers, query strings, tokens, emails or phone
// numbers; identity is the user id plus an organizationId tag.

let sentry = null;
let pendingUser;

const FILTERED = "[Filtered]";
const SENSITIVE_KEY = /pass(word)?|pwd|secret|token|auth|cookie|session|api[-_]?key|jwt|otp|totp|2fa|phone|email|mobile/i;
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const JWT_RE = /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g;
const BEARER_RE = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi;
const PHONE_RE = /(\+\d{10,15}\b)|(\(?\b\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}\b)|(\b1?\d{10}\b)/g;
const KEYVAL_RE = /\b(password|passwd|pwd|token|secret|api[-_]?key|authorization)(["']?\s*[:=]\s*["']?)[^\s"',&}]+/gi;

export function scrubString(s) {
  if (typeof s !== "string") return s;
  return s
    .replace(JWT_RE, "[jwt]")
    .replace(BEARER_RE, "$1 [token]")
    .replace(KEYVAL_RE, `$1$2${FILTERED}`)
    .replace(EMAIL_RE, "[email]")
    .replace(PHONE_RE, "[phone]");
}

function scrubDeep(value, depth = 0) {
  if (depth > 8 || value == null) return value;
  if (typeof value === "string") return scrubString(value);
  if (Array.isArray(value)) return value.map((v) => scrubDeep(v, depth + 1));
  if (typeof value === "object") {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      out[k] = SENSITIVE_KEY.test(k) ? FILTERED : scrubDeep(v, depth + 1);
    });
    return out;
  }
  return value;
}

const stripQuery = (url) => (typeof url === "string" ? url.split(/[?#]/)[0] : url);

export function scrubBreadcrumb(crumb) {
  if (!crumb) return crumb;
  const out = { ...crumb };
  if (out.message) out.message = scrubString(out.message);
  if (out.data) {
    const data = scrubDeep(out.data);
    if (data.url) data.url = stripQuery(data.url);
    if (data.from) data.from = stripQuery(data.from);
    if (data.to) data.to = stripQuery(data.to);
    delete data.arguments; // console breadcrumbs: raw logged values
    delete data.body;
    out.data = data;
  }
  return out;
}

export function scrubEvent(event) {
  if (!event) return event;
  event.user = event.user && event.user.id != null ? { id: String(event.user.id) } : undefined;
  if (event.request) {
    event.request = {
      url: stripQuery(event.request.url),
      headers: event.request.headers && event.request.headers["User-Agent"] ? { "User-Agent": event.request.headers["User-Agent"] } : undefined,
    };
  }
  if (event.message) event.message = scrubString(event.message);
  if (event.exception && Array.isArray(event.exception.values)) {
    event.exception.values.forEach((ex) => {
      ex.value = scrubString(ex.value);
      const frames = ex.stacktrace && ex.stacktrace.frames;
      if (Array.isArray(frames)) frames.forEach((f) => delete f.vars);
    });
  }
  if (Array.isArray(event.breadcrumbs)) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb).filter(Boolean);
  if (event.extra) event.extra = scrubDeep(event.extra);
  if (event.tags) event.tags = scrubDeep(event.tags);
  return event;
}

function applyUser(S, user) {
  if (!user) {
    S.setUser(null);
    S.setTag("organizationId", undefined);
    return;
  }
  const id = user.id || user._id;
  S.setUser(id ? { id: String(id) } : null);
  const org = user.organizationId && (user.organizationId._id || user.organizationId);
  S.setTag("organizationId", org ? String(org) : undefined);
}

export function initErrorTracking() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return Promise.resolve(false);
  return import("@sentry/react")
    .then((S) => {
      S.init({
        dsn,
        environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
        release: process.env.REACT_APP_GIT_COMMIT || undefined,
        sendDefaultPii: false,
        maxBreadcrumbs: 30,
        beforeSend: scrubEvent,
        beforeBreadcrumb: scrubBreadcrumb,
      });
      sentry = S;
      if (pendingUser !== undefined) applyUser(S, pendingUser);
      return true;
    })
    .catch(() => false);
}

// Call on login (user object) and logout (null). Safe before/without init.
export function setErrorTrackingUser(user) {
  pendingUser = user || null;
  if (sentry) applyUser(sentry, pendingUser);
}
